import _ from 'lodash';

import {
  ContactPointsAlertType,
  ContactPointsMessageType,
  contactPointFormIds,
} from '../traveler/profile/TravelerProfileConstants';

import {
  TravelerProfileUpdateRequest,
  ContactPoint,
  TravelerProfile,
} from '../types/travelerProfile';
import { TravelerProfileFormValues } from '../types/travelerProfileForm';
import { RowData } from '../components/table/Table';
import { DropdownSelection } from '../types/airframe';

export const transformToTravelerProfileUpdateRequest = (
  formValues: TravelerProfileFormValues,
): TravelerProfileUpdateRequest => {
  const contactPoints: ContactPoint[] = [];
  contactPoints.push(
    transformToAlertTypeContactPoint(
      formValues,
      ContactPointsAlertType.PRE_TRIP,
    ),
  );
  contactPoints.push(
    transformToAlertTypeContactPoint(
      formValues,
      ContactPointsAlertType.TRAVELING,
    ),
  );
  if (
    formValues[
      contactPointFormIds(ContactPointsAlertType.HEALTH_AND_SAFETY).email as keyof TravelerProfileFormValues
    ]
  ) {
    contactPoints.push(
      transformToAlertTypeContactPoint(
        formValues,
        ContactPointsAlertType.HEALTH_AND_SAFETY,
      ),
    );
  }
  return {
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    email: formValues.email,
    clock: formValues.preferredClock,
    contactPoints,
  };
};

const transformToContactPoint = (
  alertType: typeof ContactPointsAlertType[keyof typeof ContactPointsAlertType],
  messageType: typeof ContactPointsMessageType[keyof typeof ContactPointsMessageType],
  phone: string,
  internationalPhone: { countryCode: string; number: string },
  email: string,
  sendContactPointAlert: boolean,
): ContactPoint => {
  const contactPoint: ContactPoint = {
    alertType,
    messageType,
    isDisabled: !sendContactPointAlert,
  };

  if (messageType === ContactPointsMessageType.SMS) {
    contactPoint.countryCode = internationalPhone.countryCode;
    contactPoint.phoneNumberE164 = internationalPhone.number;
  } else {
    contactPoint.contact = email;
  }

  return contactPoint;
};

const transformToAlertTypeContactPoint = (
  formValues: TravelerProfileFormValues,
  alertType: typeof ContactPointsAlertType[keyof typeof ContactPointsAlertType],
): ContactPoint => {
  const alertTypeFormIds = contactPointFormIds(alertType);
  return transformToContactPoint(
    alertType,
    (formValues[alertTypeFormIds.messageType as keyof TravelerProfileFormValues] as DropdownSelection)?.value as keyof typeof ContactPointsMessageType,
    formValues[alertTypeFormIds.phone as keyof TravelerProfileFormValues] as string,
    formValues[alertTypeFormIds.phoneInternational as keyof TravelerProfileFormValues] as { countryCode: string; number: string },
    formValues[alertTypeFormIds.email as keyof TravelerProfileFormValues] as string,
    formValues[alertTypeFormIds.sendAlerts as keyof TravelerProfileFormValues] as boolean,
  );
};

const toUniqueCSVContact = (contacts: Array<string | null | undefined>): string =>
  _.uniq(_.compact(contacts)).join(', ');

const shouldIncludeTravelerProfile = (
  contactPoints: Array<ContactPoint>,
  searchContacts: Array<string>,
): boolean =>
  contactPoints.some(contactPoint => {
    return (
      _.isEmpty(searchContacts) ||
      (contactPoint.contact &&
        searchContacts
          .map(searchContact => searchContact.toUpperCase())
          .includes(contactPoint.contact.toUpperCase())) ||
      (contactPoint.phoneNumberE164 &&
        searchContacts.includes(contactPoint.phoneNumberE164))
    );
  });

const transformToTravelerProfileRow = (
  key: number,
  travelerProfile: TravelerProfile,
): RowData | null => {
  const travelerKey = travelerProfile.travelerKey || '';
  const emails: Array<string | null | undefined> = travelerProfile.contactPoints.map(point => {
    if (point.contact && point.messageType === ContactPointsMessageType.EMAIL) {
      return point.contact;
    }
    return null;
  });

  const emailsWithoutDuplicates = toUniqueCSVContact(emails);

  const sms: Array<string | null | undefined> = travelerProfile.contactPoints.map(point => {
    if (
      point.phoneNumberE164 &&
      point.messageType === ContactPointsMessageType.SMS
    ) {
      return point.phoneNumberE164;
    }
    return null;
  });

  const smsWithoutDuplicates = toUniqueCSVContact(sms);

  return {
    key,
    values: [
      travelerKey,
      travelerProfile.firstName,
      travelerProfile.lastName,
      emailsWithoutDuplicates,
      smsWithoutDuplicates,
    ],
  };
};

// When we pass email as a queryString param, the backend performs a full-text search on profiles.full_text column
// instead of querying the actual (contactPoints.contact) email field. You are likely to get more matches because of the
// full_text search. This filter is in place to make sure we only include the profiles.contactPoints.contact where
// emails are an exact match (ignore case) with the search criteria.
export const filterAndtransformToTravelerProfileTableRows = (
  travelerProfiles: Array<TravelerProfile>,
  searchContacts: Array<string | null | undefined>,
): Array<RowData> =>
  _.compact(
    travelerProfiles
      .filter(profile =>
        shouldIncludeTravelerProfile(
          profile.contactPoints,
          _.compact(searchContacts),
        ),
      )
      .map((value, key) => transformToTravelerProfileRow(key, value)),
  );